import {
  Button,
  DialogActions,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  FormControl,
  Autocomplete,
  TextField,
  CircularProgress,
  Stack,
} from "@mui/material";
import { SiteOption } from "./types";
import { useEffect, useState } from "react";
import { useWebApiV1CreateOrderMutation } from "@providers/hop-ord-server/api";
import { useNavigate } from "react-router-dom";

interface Props {
  open: boolean;
  onClose: () => void;
  treatingClinics: SiteOption[];
}

const CreateCareplanModal = ({ open, onClose, treatingClinics }: Props) => {
  const navigate = useNavigate();
  const [addOrder] = useWebApiV1CreateOrderMutation();
  const [clinicError, setClinicError] = useState<string>("");
  const [selectedClinic, setSelectedClinic] = useState<SiteOption | undefined>(
    undefined,
  );
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const createOrder = async (tenantId: number) => {
    setIsCreatingOrder(true);
    if (!selectedClinic) {
      setClinicError("This field is required");
      return;
    }
    try {
      const order = await addOrder({
        tenantId,
        treatingDepartment: selectedClinic.value,
      }).unwrap();
      navigate(`/order/${order}`);
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const handleClose = () => {
    onClose();
    if (treatingClinics?.length !== 1) {
      setSelectedClinic(undefined);
    }
    setClinicError("");
  };

  const handleContinue = async () => {
    if (!selectedClinic) {
      setClinicError("This field is required");
      return;
    }
    const tenantId = selectedClinic.tenantId;
    await createOrder(tenantId);
    handleClose();
  };

  useEffect(() => {
    if (treatingClinics?.length === 1) {
      setSelectedClinic(treatingClinics[0]);
    }
  }, [treatingClinics]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Create Careplan</DialogTitle>
      <DialogContent>
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Typography variant="body1">
            Select the treating clinic you are creating for.{" "}
          </Typography>
          <FormControl error={!!clinicError} fullWidth>
            <Autocomplete
              id="clinic-select"
              disableClearable
              options={treatingClinics}
              value={selectedClinic ?? { label: "", value: "", tenantId: 0 }}
              onChange={(_, newValue) => {
                setSelectedClinic(newValue);
                setClinicError("");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Treating clinic*"
                  error={!!clinicError}
                  helperText={clinicError}
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option.label === value.label
              }
              getOptionLabel={(option) => option.label}
            />
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          sx={{ width: 80 }}
          onClick={handleClose}
          disabled={isCreatingOrder}
        >
          Cancel
        </Button>
        <Button
          sx={{ width: 80 }}
          onClick={handleContinue}
          disabled={isCreatingOrder}
        >
          {!isCreatingOrder ? "Continue" : <CircularProgress size={24} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCareplanModal;
